import { SocialNetworksId, socialSVGs } from "@/interfaces";
import React from "react";

const brandIconColors: Record<string, string> = {
  LI: "text-[#0A66C2]", // LinkedIn Blue
  FB: "text-[#1877f2]", // Facebook Blue
  TW: "text-black dark:text-white", // Twitter/X Blue
  IG: "text-[#E4405F]", // Instagram Pink/Red
  TT: "text-black dark:text-white", // TikTok
  ZL: "text-[#0041D9]", // Zillow Blue
  BE: "text-[#0D6EFD]", // Birdeye Blue
  IF: "text-slate-700", // Internal Slate
  EX: "text-[#1B62A9]", // Experience.com Blue
};

interface SocialSvgProps {
  networkId: SocialNetworksId;
  size?: "xs" | "sm" | "md" | "lg";
  variant?: "badge" | "icon";
  iconColorClass?: string;
  bgColorClass?: string;
  onClick?: (e: React.MouseEvent) => void;
}

const sizes = {
  xs: { badge: "h-4 w-4", icon: "h-4 w-4" },
  sm: { badge: "h-12 w-12", icon: "h-6 w-6" },
  md: { badge: "h-16 w-16", icon: "h-8 w-8" },
  lg: { badge: "h-20 w-20", icon: "h-10 w-10" },
};

export function SocialSvg({
  networkId,
  size = "md",
  variant = "badge",
  iconColorClass,
  bgColorClass,
  onClick,
}: SocialSvgProps) {
  const Icon = socialSVGs[networkId];
  if (!Icon) {
    console.warn(`Icon for social network '${networkId}' not found.`);
    return null;
  }

  const currentSize = sizes[size];
  const finalIconColor = iconColorClass || brandIconColors[networkId as string] || "text-slate-600";
  // googleColor.svg uses multiple colors. mask and bg-current below would flatten it to one color.
  // SC logo is a PNG, so mask wouldn't apply correctly.
  const useImage = (networkId === "GL" || networkId === "SC" || networkId === "BE") && !iconColorClass;

  const iconClassName = `${currentSize.icon} ${variant === "icon" ? bgColorClass ?? "" : ""}`.trim();
  const icon = useImage ? (
    <img src={Icon} className={`${iconClassName} object-contain`} alt="" />
  ) : (
    <div
      className={`${iconClassName} bg-current transition-colors ${finalIconColor}`}
      style={{
        maskImage: `url(${Icon})`,
        WebkitMaskImage: `url(${Icon})`,
        maskSize: "contain",
        maskRepeat: "no-repeat",
        maskPosition: "center",
      }}
      aria-label={`${networkId} icon`}
    />
  );

  if (variant === "icon") {
    if (!onClick) return icon;
    return (
      <button type="button" onClick={onClick} className="inline-flex cursor-pointer">
        {icon}
      </button>
    );
  }

  const badgeClassName = `flex ${currentSize.badge} items-center justify-center rounded-xl transition-all ${
    bgColorClass ?? "bg-slate-50"
  } ${onClick ? "cursor-pointer hover:bg-slate-200 hover:scale-105 active:scale-95 shadow-xs" : ""}`;

  return (
    <div onClick={onClick} className={badgeClassName}>
      {icon}
    </div>
  );
}
